import { useEffect, useRef, useCallback } from "react";
import { toast } from "sonner";

/**
 * useUndoableDelete
 * ─────────────────
 * Shared optimistic-delete-with-Undo-toast pattern used everywhere
 * the user can erase something they own (broadcasts, posts, comments,
 * bookmarks, playlists, threads, room messages, prayers, etc.).
 *
 * Behavior — identical across every surface:
 *   1. UI calls `remove(id, optionalRow)`.
 *   2. The hook IMMEDIATELY removes the item from the local list via
 *      the caller-supplied `setList` updater (optimistic UI).
 *   3. A sonner toast appears with `successLabel` + an Undo action.
 *   4. If the user taps Undo within `graceMs` (default 5s), the row
 *      is restored at its original index and NO network call fires.
 *   5. Otherwise after `graceMs` the real `apiDelete(id, row)` runs.
 *      If it fails (network, 4xx, etc.) the row is restored and an
 *      error toast surfaces.
 *   6. If the component unmounts before the grace window elapses,
 *      pending deletes are flushed in-flight to honor user intent.
 *
 * Implementation note — why this looks the way it does:
 *   React 18 invokes functional state updaters during reconciliation,
 *   NOT synchronously inside the call to setList. So you CANNOT read
 *   variables a `setList(prev => ...)` updater mutates immediately
 *   after the call. To capture `originalIndex` reliably without
 *   asking every caller to track the list themselves, we use the
 *   functional updater to extract the snapshot AND queue the toast
 *   from inside it (the updater always runs at least once, even in
 *   StrictMode double-invoke we guard with `armedRef`). This keeps
 *   the API surface tiny (`remove(id, row?)`) and works without
 *   forcing the parent to pass refs.
 */
export function useUndoableDelete({
  setList,
  keyOf,
  apiDelete,
  successLabel = "Deleted",
  errorLabel = "Couldn't delete — please try again",
  graceMs = 5000,
}) {
  // pendingRef holds in-flight grace-window timers keyed by id so
  // (a) we can cancel them on Undo and (b) we can flush them all on
  // unmount.
  const pendingRef = useRef({});

  // Flush on unmount: anything still in the grace window is committed
  // immediately. This matches the Lives-tab behavior the user
  // approved — if you tap delete and leave the page, you meant it.
  useEffect(() => {
    return () => {
      const pending = pendingRef.current;
      Object.entries(pending).forEach(([id, p]) => {
        clearTimeout(p.timer);
        // Fire-and-forget — page is unmounting anyway.
        try { apiDelete(id, p.row); } catch { /* ignore */ }
      });
      pendingRef.current = {};
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const remove = useCallback((id, row) => {
    if (!id) return;
    // Guard against React StrictMode double-invoking the functional
    // updater (which would fire two toasts and two timers for one
    // user click). We only arm side effects on the first invocation.
    let armed = true;

    setList((prev) => {
      const list = prev || [];
      const originalIndex = list.findIndex((it) => keyOf(it) === id);
      if (originalIndex < 0) return prev;
      const snapshotRow = row || list[originalIndex];

      if (armed) {
        armed = false;
        // Schedule the network DELETE after the grace window.
        const timer = setTimeout(async () => {
          try {
            await apiDelete(id, snapshotRow);
          } catch (e) {
            // Restore on failure so the UI doesn't silently lie.
            setList((cur) => {
              const arr = cur || [];
              if (arr.some((it) => keyOf(it) === id)) return cur;
              const restored = [...arr];
              restored.splice(Math.max(0, Math.min(originalIndex, restored.length)), 0, snapshotRow);
              return restored;
            });
            const detail = e?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : errorLabel);
          } finally {
            delete pendingRef.current[id];
          }
        }, graceMs);

        pendingRef.current[id] = { row: snapshotRow, originalIndex, timer };

        toast.success(successLabel, {
          duration: graceMs,
          action: {
            label: "Undo",
            onClick: () => {
              const pending = pendingRef.current[id];
              if (!pending) return;
              clearTimeout(pending.timer);
              delete pendingRef.current[id];
              setList((cur) => {
                const arr = cur || [];
                if (arr.some((it) => keyOf(it) === id)) return cur;
                const restored = [...arr];
                restored.splice(Math.max(0, Math.min(pending.originalIndex, restored.length)), 0, pending.row);
                return restored;
              });
            },
          },
        });
      }

      return list.filter((it) => keyOf(it) !== id);
    });
  }, [setList, keyOf, apiDelete, successLabel, errorLabel, graceMs]);

  return remove;
}

/**
 * useUndoableBulkDelete
 * ─────────────────────
 * Same UX pattern, but for a "Clear all" sweep — used by the Lives
 * tab clear-all and reusable for any other "wipe everything in this
 * list" action.
 */
export function useUndoableBulkDelete({
  setList,
  apiBulkDelete,
  successLabel = "Cleared",
  errorLabel = "Couldn't clear — please try again",
  graceMs = 5000,
  keepPredicate = null,
}) {
  const pendingRef = useRef(null);

  useEffect(() => {
    return () => {
      const pending = pendingRef.current;
      if (pending) {
        clearTimeout(pending.timer);
        try { apiBulkDelete(); } catch { /* ignore */ }
        pendingRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearAll = useCallback(() => {
    let armed = true;
    setList((prev) => {
      const snapshot = prev || [];
      let removedCount = 0;
      let nextList;
      if (keepPredicate) {
        const kept = snapshot.filter(keepPredicate);
        removedCount = snapshot.length - kept.length;
        nextList = kept;
      } else {
        removedCount = snapshot.length;
        nextList = [];
      }
      if (armed && removedCount > 0) {
        armed = false;
        const timer = setTimeout(async () => {
          try {
            await apiBulkDelete();
          } catch (e) {
            setList(snapshot);
            const detail = e?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : errorLabel);
          } finally {
            pendingRef.current = null;
          }
        }, graceMs);

        pendingRef.current = { snapshot, timer };

        toast.success(`${successLabel}${removedCount > 1 ? ` ${removedCount} items` : ""}`, {
          duration: graceMs,
          action: {
            label: "Undo",
            onClick: () => {
              const pending = pendingRef.current;
              if (!pending) return;
              clearTimeout(pending.timer);
              pendingRef.current = null;
              setList(pending.snapshot);
            },
          },
        });
      }
      return nextList;
    });
  }, [setList, apiBulkDelete, successLabel, errorLabel, graceMs, keepPredicate]);

  return clearAll;
}
